<script lang="ts">
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import type { Fact } from '$lib/components/ui/factsCard/fact';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import type { Component } from 'svelte';
	import IconChevronDown from '~icons/mdi/chevron-down';
	import IconChevronUp from '~icons/mdi/chevron-up';

	const {
		id,
		facts = [],
		sectionTitle,
		Icon,
		initialCount = 3
	}: {
		id?: string;
		facts: Fact[];
		sectionTitle: string;
		Icon: Component;
		initialCount?: number;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	let expanded = $state(false);
	let cardRef = $state<HTMLElement | null>(null);

	const initialFacts = $derived(facts.slice(0, initialCount));
	const remainingFacts = $derived(facts.slice(initialCount));
	const hiddenCount = $derived(facts.length - initialCount);

	const formatIndex = (idx: number) => (idx + 1).toString().padStart(2, '0');

	function handleToggle() {
		analyticsApi.sendAnalytics({ event: ANALYTICS_EVENT_NAMES.readMore, payload: { type: 'facts' } });

		if (expanded) {
			expanded = false;
			setTimeout(() => {
				cardRef?.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}, 260);
		} else {
			expanded = true;
		}
	}
</script>

{#if facts.length > 0}
	<section
		bind:this={cardRef}
		{id}
		class="card scroll-mt-24 border border-base-200 bg-base-200/40 shadow-sm"
		aria-labelledby="facts-heading"
	>
		<div class="card-body p-4 sm:p-5">
			<div class="flex items-center justify-between pb-1">
				<h3 id="facts-heading" class="card-title flex items-center gap-2 text-base font-bold tracking-tight text-base-content">
					<Icon />
					<span>{sectionTitle}</span>
				</h3>
				<span class="badge gap-1.5 badge-soft py-3 font-mono text-xs font-medium tracking-wide badge-secondary">
					{facts.length}
				</span>
			</div>

			<ol class="m-0 flex list-none flex-col gap-2.5 p-0">
				{#each initialFacts as fact, index (fact.content)}
					<li class="rounded-box border border-base-300/70 bg-base-100 p-3.5 transition-colors hover:border-base-content/20">
						<div class="flex items-start gap-3">
							<span class="shrink-0 pt-0.5 font-mono text-xs font-semibold text-base-content/70 select-none">
								{formatIndex(index)}
							</span>
							<div class="min-w-0 flex-1">
								{#if fact.title}
									<h4 class="mb-1 text-sm leading-snug font-semibold text-base-content">
										{fact.title}
									</h4>
								{/if}
								<p class="text-xs leading-relaxed text-base-content/85 sm:text-sm">
									{fact.content}
								</p>
							</div>
						</div>
					</li>
				{/each}
			</ol>

			{#if hiddenCount > 0}
				<div
					class={{
						'grid transition-[grid-template-rows,opacity] duration-250 ease-out': true,
						'mt-2.5 grid-rows-[1fr] opacity-100': expanded,
						'pointer-events-none mt-0 grid-rows-[0fr] opacity-0': !expanded
					}}
				>
					<div class="overflow-hidden">
						<ol start={initialCount + 1} class="m-0 flex list-none flex-col gap-2.5 p-0">
							{#each remainingFacts as fact, index (fact.content)}
								<li
									class="rounded-box border border-base-300/70 bg-base-100 p-3.5 transition-colors hover:border-base-content/20"
								>
									{@render FactItem(fact, index)}
								</li>
							{/each}
						</ol>
					</div>
				</div>

				<div class="pt-2">
					{@render ToggleBtn()}
				</div>
			{/if}
		</div>
	</section>
{/if}

{#snippet FactItem(fact: Fact, index: number)}
	<div class="flex items-start gap-3">
		<span class="shrink-0 pt-0.5 font-mono text-xs font-semibold text-primary/70 select-none">
			{formatIndex(initialCount + index)}
		</span>
		<div class="min-w-0 flex-1">
			{#if fact.title}
				<h4 class="mb-1 text-sm leading-snug font-semibold text-base-content">
					{fact.title}
				</h4>
			{/if}
			<p class="text-xs leading-relaxed text-base-content/85 sm:text-sm">
				{fact.content}
			</p>
		</div>
	</div>
{/snippet}

{#snippet ToggleBtn()}
	<button class="btn w-full gap-1 rounded-box btn-primary btn-sm" onclick={handleToggle} aria-expanded={expanded}>
		{#if expanded}
			<span>Show less</span>
			<IconChevronUp />
		{:else}
			<span>Show {hiddenCount} more</span>
			<IconChevronDown />
		{/if}
	</button>
{/snippet}
